import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';

const formatTimestamp = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const sourceColor = (source) => {
  if (source === 'ai') {
    return 'info';
  }
  if (source === 'restore') {
    return 'warning';
  }
  return 'default';
};

const PluginVersionsPanel = ({
  versions = [],
  loading = false,
  error = '',
  onRefresh,
  onRestore,
  restoringVersionId = '',
  t,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 1 }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('global_setting.plugins_versions_title', 'Saved versions')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'global_setting.plugins_versions_description',
            'Every saved change is kept here. Restoring a version also saves the current state first.'
          )}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />}
        onClick={onRefresh}
        disabled={loading}
      >
        {t('global_setting.plugins_versions_refresh', 'Refresh')}
      </Button>
    </Stack>

    {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}

    {loading && !versions.length ? (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    ) : null}

    {!loading && !versions.length ? (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <HistoryIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('global_setting.plugins_versions_empty', 'No saved version exists yet.')}
        </Typography>
      </Paper>
    ) : null}

    {versions.length ? (
      <Stack spacing={1} sx={{ overflowY: 'auto', pr: 0.5, minHeight: 0 }}>
        {versions.map((version) => {
          const source = String(version?.source || 'manual').toLowerCase();
          const isRestoring = restoringVersionId === version?.id;
          return (
            <Paper key={version?.id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Chip
                    size="small"
                    color={sourceColor(source)}
                    label={t(
                      `global_setting.plugins_versions_source_${source}`,
                      source === 'ai' ? 'AI save' : source === 'restore' ? 'Before restore' : 'Manual save'
                    )}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('global_setting.plugins_versions_number', 'Version {{number}}', {
                      number: version?.versionNumber || '?',
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(version?.createdAt)}
                  </Typography>
                  {version?.actor ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('global_setting.plugins_versions_by', 'by {{actor}}', { actor: version.actor })}
                    </Typography>
                  ) : null}
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={isRestoring ? <CircularProgress size={15} /> : <RestoreIcon />}
                  disabled={loading || Boolean(restoringVersionId)}
                  onClick={() => onRestore(version)}
                >
                  {isRestoring
                    ? t('global_setting.plugins_versions_restoring', 'Restoring...')
                    : t('global_setting.plugins_versions_restore', 'Restore')}
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    ) : null}
  </Box>
);

export default PluginVersionsPanel;
